import "./App.css";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "sonner";
import { Play, Pause } from "lucide-react";
import { SiteProvider } from "./context/SiteContext";
import { AudioProvider, useAudio } from "./context/AudioContext";
import { CartProvider } from "./context/CartContext";

import Navbar from "./components/em/Navbar";
import Footer from "./components/em/Footer";
import Scanlines from "./components/em/Scanlines";
import Landing from "./components/em/Landing";
import ProducersSuite from "./components/em/ProducerSuite";
import Headphones from "./components/em/Headphones";
import Beatbox from "./components/em/Beatbox";
import Soundscape from "./components/em/Soundscape";
import TheUnknown from "./components/em/TheUnknown";
import Store from "./components/em/Store";
import Admin from "./components/em/Admin";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function PersistentPlayer() {
  const { src, playing, progress, duration, toggle, seek } = useAudio();
  if (!src) return null;
  const fmt = (s) => {
    if (!s || !isFinite(s)) return "--:--";
    return `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
  };
  const cur = duration ? (progress / 100) * duration : 0;
  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-md border-t border-[#27272A]">
      <div className="max-w-[1400px] mx-auto px-6 py-3 flex items-center gap-4">
        <button onClick={toggle} className="bg-neon text-black p-2 hover:bg-[#00CC55] transition-colors flex-shrink-0">
          {playing ? <Pause size={14} /> : <Play size={14} />}
        </button>
        <div className="flex-1 cursor-pointer" onClick={(e) => { const r = e.currentTarget.getBoundingClientRect(); seek((e.clientX - r.left) / r.width); }}>
          <div className="h-1 bg-[#27272A] relative">
            <div className="h-full bg-neon transition-all" style={{ width: `${progress}%` }} />
          </div>
        </div>
        <span className="font-mono text-xs text-neutral-400 flex-shrink-0">
          {fmt(cur)} / {fmt(duration)}
        </span>
      </div>
    </div>
  );
}

function App() {
  return (
    <div className="App min-h-screen bg-[#0A0A0A] text-white">
      <BrowserRouter>
        <SiteProvider>
          <CartProvider>
          <AudioProvider>
            <ScrollToTop />
            <Scanlines />
            <Navbar />
            <main data-testid="app-main">
              <Routes>
                <Route path="/" element={<Landing />} />
                <Route path="/producers-suite" element={<ProducersSuite />} />
                <Route path="/headphones" element={<Headphones />} />
                <Route path="/beatbox" element={<Beatbox />} />
                <Route path="/soundscape" element={<Soundscape />} />
                <Route path="/the-unknown" element={<TheUnknown />} />
                <Route path="/unknown-signals" element={<TheUnknown />} />
                <Route path="/store" element={<Store />} />
                <Route path="/admin" element={<Admin />} />
                <Route
                  path="*"
                  element={
                    <div className="min-h-[60vh] flex flex-col items-center justify-center px-6">
                      <div className="font-display font-black text-8xl text-neon/30">404</div>
                      <p className="label-mono text-neutral-500 mt-4">// signal not found</p>
                      <Link to="/" className="btn-primary mt-8 inline-flex items-center gap-2">
                        RETURN HOME
                      </Link>
                    </div>
                  }
                />
              </Routes>
            </main>
            <PersistentPlayer />
            <Footer />
            <Toaster
              theme="dark"
              position="bottom-right"
              toastOptions={{
                style: {
                  background: "#0A0A0A",
                  border: "1px solid #27272A",
                  color: "#fff",
                  borderRadius: 0,
                  fontFamily: "JetBrains Mono, monospace",
                  fontSize: 12,
                },
              }}
            />
          </AudioProvider>
          </CartProvider>
        </SiteProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
